/**
 * Liquid edge — composite sine-wave edge line with kill distortion,
 * proximity reach, dark band, core pulse, highlight, and proximity glow.
 */

import type { Graphics } from 'pixi.js';
import { VOID } from '../../constants';
import type { VoidState } from './types';
import { visibleX, getVoidTheme } from './types';

/** Get the liquid edge Y at a given x position (composite sine waves + kill distortion + proximity reach). */
export function getEdgeY(x: number, baseY: number, state: VoidState): number {
  const t = state.edgeTime;
  const amp = state.edgeWaveAmp;

  // 3 composite sine waves
  let y = baseY
    + Math.sin(x * 0.008 + t * 1.5) * 2 * amp      // slow swell
    + Math.sin(x * 0.015 + t * -2.0) * 1.5 * amp    // counter-direction
    + Math.sin(x * 0.025 + t * 3.0) * 0.8 * amp;    // surface ripple

  // Kill distortion — gaussian bump
  if (state.edgeDistortionStrength > 0.5) {
    const dx = x - state.edgeDistortionX;
    const sigma = VOID.EDGE_DISTORTION_SIGMA;
    const bump = state.edgeDistortionStrength * Math.exp(-(dx * dx) / (2 * sigma * sigma));
    y -= bump; // upward = negative Y
  }

  // Proximity reach — gaussian bumps toward nearby alive players
  for (const p of state.playerPositions) {
    if (!p.alive) continue;
    const dy = p.y - baseY;
    if (dy > VOID.PROXIMITY_Y_RANGE || dy < -VOID.PROXIMITY_Y_RANGE) continue;
    const dx = x - p.x;
    if (Math.abs(dx) > VOID.PROXIMITY_X_RANGE * 2) continue;
    const sigma = VOID.PROXIMITY_REACH_SIGMA;
    const proximityT = Math.max(0, 1 - Math.abs(dy) / VOID.PROXIMITY_Y_RANGE);
    const bump = VOID.PROXIMITY_REACH_PX * proximityT * Math.exp(-(dx * dx) / (2 * sigma * sigma));
    y -= bump; // reach upward
  }

  return y;
}

/** Interpolate edge Y from cached points — cheap linear lookup, <1px accuracy. */
export function getInterpolatedEdgeY(x: number, state: VoidState): number {
  const segIdx = x / state.cachedEdgeSegWidth;
  const i = Math.floor(segIdx);
  const frac = segIdx - i;
  const a = state.cachedEdgeYs[Math.min(i, state.cachedEdgeYs.length - 1)];
  const b = state.cachedEdgeYs[Math.min(i + 1, state.cachedEdgeYs.length - 1)];
  return a + (b - a) * frac;
}

export function drawLiquidEdge(gfx: Graphics, voidY: number, state: VoidState): void {
  gfx.clear();

  const v = visibleX();
  const segCount = VOID.EDGE_SEGMENTS;
  const segWidth = v.w / segCount;

  // Build polyline points once (reused for all 4 strokes)
  const points: { x: number; y: number }[] = [];
  for (let i = 0; i <= segCount; i++) {
    const x = v.off + i * segWidth;
    const y = getEdgeY(x, voidY, state);
    points.push({ x, y });
  }

  // Cache edge Y values for bubble interpolation
  state.cachedEdgeYs.length = 0;
  state.cachedEdgeSegWidth = segWidth;
  for (const pt of points) state.cachedEdgeYs.push(pt.y);

  // 1. Dark backing band (Sakurai dark framing) — offset below
  gfx.moveTo(points[0].x, points[0].y + VOID.EDGE_DARK_BAND_OFFSET);
  for (let i = 1; i < points.length; i++) {
    gfx.lineTo(points[i].x, points[i].y + VOID.EDGE_DARK_BAND_OFFSET);
  }
  gfx.stroke({ color: VOID.EDGE_DARK_BAND_COLOR, width: VOID.EDGE_DARK_BAND_WIDTH, alpha: VOID.EDGE_DARK_BAND_ALPHA });

  // 2. Main edge — color from active theme
  const edgeColor = getVoidTheme().voidEdgeColor;
  gfx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length; i++) {
    gfx.lineTo(points[i].x, points[i].y);
  }
  gfx.stroke({ color: edgeColor, width: 2, alpha: 1 });

  // 3. White core line — pulsing luminous beam (Travis/Shulk feel)
  const coreAlpha = VOID.EDGE_CORE_PULSE_BASE
    + Math.sin(state.edgeTime * VOID.EDGE_CORE_PULSE_SPEED) * VOID.EDGE_CORE_PULSE_AMP
    + state.riseIntensity * VOID.EDGE_CORE_RISE_BOOST;
  gfx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length; i++) {
    gfx.lineTo(points[i].x, points[i].y);
  }
  gfx.stroke({ color: 0xffffff, width: 1, alpha: Math.max(0, Math.min(1, coreAlpha)) });

  // 4. Surface highlight — faint line above main edge
  const hlOffset = VOID.EDGE_HIGHLIGHT_OFFSET;
  gfx.moveTo(points[0].x, points[0].y - hlOffset);
  for (let i = 1; i < points.length; i++) {
    gfx.lineTo(points[i].x, points[i].y - hlOffset);
  }
  gfx.stroke({ color: 0xffaaff, width: 1, alpha: VOID.EDGE_HIGHLIGHT_ALPHA });

  // Proximity glow overlay — brighter edge segments near alive players
  for (const p of state.playerPositions) {
    if (!p.alive) continue;
    const dy = p.y - voidY;
    if (dy > VOID.PROXIMITY_Y_RANGE || dy < -VOID.PROXIMITY_Y_RANGE) continue;
    const proximityT = Math.max(0, 1 - Math.abs(dy) / VOID.PROXIMITY_Y_RANGE);
    if (proximityT < 0.05) continue;

    // Find segment range near player.x
    const centerSeg = Math.floor(p.x / segWidth);
    const halfRange = Math.ceil(VOID.PROXIMITY_X_RANGE / segWidth);
    const startSeg = Math.max(0, centerSeg - halfRange);
    const endSeg = Math.min(segCount, centerSeg + halfRange);

    if (startSeg >= points.length || endSeg < 0) continue;

    gfx.moveTo(points[startSeg].x, points[startSeg].y);
    for (let i = startSeg + 1; i <= endSeg && i < points.length; i++) {
      gfx.lineTo(points[i].x, points[i].y);
    }
    gfx.stroke({
      color: edgeColor,
      width: VOID.PROXIMITY_GLOW_WIDTH,
      alpha: VOID.PROXIMITY_GLOW_BOOST * proximityT,
    });
  }
}
